import { ref, computed, unref } from 'vue';

export function useElementStyles(settings, externalActive) {
	const isHovered = ref(false);
	const isActive = ref(false);

	function addPxIfNumber(value) {
		return isNaN(Number(value)) ? value : `${value}px`;
	}

	function isValidColor(color) {
		if (typeof color !== 'string') {
			return false;
		}

		const hexRegex = /^#([0-9A-Fa-f]{3,4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;
		const rgbRegex = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/;
		const rgbaRegex = /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(0(\.\d+)?|1(\.0+)?)\s*\)$/;
		const transparentRegex = /^transparent$/i;

		return (
			hexRegex.test(color) ||
			rgbRegex.test(color) ||
			rgbaRegex.test(color) ||
			transparentRegex.test(color)
		);
	}

	function getColorOrVariable(color) {
		if (isValidColor(color)) {
			return color;
		} else if (color) {
			return `var(--palette-${color})`;
		}
		return undefined;
	}

	const getStylesFromSettings = (s = {}) => {
		if (!s) return {};

		const {
			spacing = {},
			size = {},
			typography = {},
			background = [],
			backgroundDirection = 'to bottom',
			backgroundImage,
			border = {},
			boxShadow = {},
		} = s;

		const resolvedColors = background.map(getColorOrVariable);

		const raw = {
			padding: spacing.padding ? spacing.padding.map(p => `${p}px`).join(' ') : undefined,
			margin: spacing.margin ? spacing.margin.map(m => `${m}px`).join(' ') : undefined,

			width: addPxIfNumber(size.width),
			height: addPxIfNumber(size.height),
			minWidth: addPxIfNumber(size.minWidth),
			minHeight: addPxIfNumber(size.minHeight),
			maxWidth: addPxIfNumber(size.maxWidth),
			maxHeight: addPxIfNumber(size.maxHeight),

			fontFamily: typography.fontFamily,
			fontWeight: typography.fontWeight,
			fontSize: typography.fontSize ? `${typography.fontSize}px` : undefined,
			lineHeight: typography.lineHeight,
			color: getColorOrVariable(typography.color),

			backgroundColor: resolvedColors.length === 1 ? resolvedColors[0] : undefined,
			backgroundImage:
				resolvedColors.length > 1
					? `linear-gradient(${backgroundDirection}, ${resolvedColors.join(', ')})`
					: backgroundImage
						? `url(${backgroundImage})`
						: undefined,
			backgroundRepeat: backgroundImage || resolvedColors.length > 1 ? 'no-repeat' : undefined,
			backgroundSize: backgroundImage || resolvedColors.length > 1 ? 'cover' : undefined,

			borderWidth: border.borderWidth !== undefined ? `${border.borderWidth}px` : undefined,
			borderRadius: border.borderRadius !== undefined ? `${border.borderRadius}px` : undefined,
			borderColor: getColorOrVariable(border.color),
			borderStyle: border.borderWidth ? 'solid' : undefined,

			boxShadow: boxShadow.value
				? `${boxShadow.value.join('px ')}px ${getColorOrVariable(boxShadow.color) || 'transparent'}`
				: undefined,
		};

		return Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== undefined));
	};

	const base = computed(() => getStylesFromSettings(unref(settings)));
	// const hover = computed(() => getStylesFromSettings(unref(settings)?.interactions?.hover));
	const active = computed(() => getStylesFromSettings(unref(settings)?.interactions?.active));

	const styles = computed(() => {
		const isExternallyActive = unref(externalActive);

		if (isExternallyActive || isActive.value || isHovered.value) {
			return { ...base.value, ...active.value };
		} else {
			return base.value;
		}
	});

	return {
		styles,
		isHovered,
		isActive,
		getColorOrVariable
	};
}